import { Component, ViewEncapsulation, output, inject, input } from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { noop } from 'rxjs';

import { ApFormErrorsComponent, ErrorMessages } from '@ap/form/errors';
import { ApIconComponent } from '@ap/icon';
import { ApContentModule, ApContentService } from '@ap/content';

import { ApSelectorOption, ApSelectorSelectionType } from '../selector.modal';

import content from './selector-select.content.json';

@Component({
    selector: 'ap-selector-select',
    templateUrl: './selector-select.component.html',
    styleUrl: './selector-select.component.scss',
    encapsulation: ViewEncapsulation.Emulated,
    imports: [ApContentModule, ApFormErrorsComponent, ApIconComponent]
})
export class ApSelectorSelectComponent implements ControlValueAccessor {
    protected control = inject(NgControl, { self: true, optional: true });
    private readonly contentService = inject(ApContentService);

    /**
     * Label of select
     */
    public readonly label = input.required<string>();

    /**
     * Selector options
     */
    public readonly options = input<ApSelectorOption[]>(undefined);

    /**
     * Selected option object
     */
    public readonly selectedOptions = input<ApSelectorOption[]>(undefined);

    /**
     * Error messages for ApFormErrors
     */
    public readonly messages = input<ErrorMessages>(undefined);

    /**
     * Content key of title to render when multiple items are selected
     */
    public readonly multipleTitle = input('selectorSelect.multiple.title');

    /**
     * Content key of text to render when multiple items are selected
     */
    public readonly multipleText = input('selectorSelect.multiple.text');

    /**
     * Type of selection allowed for user.
     * - `single` - user can select one account
     * - `multiple` - user can select one, multiple or all accounts
     */
    public readonly selectionType = input<ApSelectorSelectionType>('single');

    /**
     * If provided, this function will trigger when the item is clicked
     */
    public readonly clicked = output<void>();

    /**
     * Unique id of field that can be used to map aria fields
     */
    protected uniqueId = `selector-${Math.round(Math.random() * 10000000)}`;

    protected onChange: (value: any) => void = () => {};
    protected onTouched: () => void = noop;

    public value: any;

    constructor() {
        this.contentService.addContentItems(content);
        this.control && (this.control.valueAccessor = this);
    }

    /**
     * On blur set control to touched state
     */
    protected handleBlur(): void {
        if (!this.control) {
            return;
        }
        const { control } = this.control;
        control.markAsTouched();
    }

    /**
     * Check passed Key.
     * If `Enter` or `Space` emit open event
     */
    protected keyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            this.clicked.emit(undefined);
        }
    }

    /**
     * Is this field required
     */
    protected get isRequired(): boolean {
        if (!this.control?.control) {
            return false;
        }
        const { control } = this.control;
        return control.hasValidator(Validators.required);
    }

    /**
     * Check if select should be disabled. Disabled if:
     * - 1 or no options
     * - Formcontrol is passed and disabled
     */
    protected get isDisabled(): boolean {
        const options = this.options();
        if (!options || (options.length < 2 && !this.hasSpaarpot())) {
            return true;
        }

        if (this.control?.control) {
            const { control } = this.control;
            return control.disabled;
        }

        return false;
    }

    /**
     * Check if value is set and options is single entry
     */
    protected isSingleOptionValue(): boolean {
        const { value } = this.control || {};
        const isArray = Array.isArray(value);
        return this.options()?.length < 2 && !!(isArray ? value.length : value) && !this.hasSpaarpot();
    }

    /**
     * Check if option contain any spaarpotten objects
     * @protected
     */
    protected hasSpaarpot() {
        return JSON.stringify(this.options()).includes('spaarpotten');
    }
    /**
     * Check if passed option is selected
     */
    protected isSelected(option: ApSelectorOption): boolean {
        const selectedOptions = this.selectedOptions();
        if (!selectedOptions) {
            return false;
        }
        return selectedOptions.find((selectedOption) => selectedOption.id === option.id) !== undefined;
    }

    /**
     * Control value accessor function
     * Registers on change event
     * @internal
     */
    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    /**
     * Control value accessor function
     * Registers on touched event
     * @internal
     */
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Control value accessor function
     * Write value
     * @internal
     */
    public writeValue(value: any): void {
        this.value = value;
    }
}
